/** Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/

import type { Dayjs, ManipulateType, OpUnitType } from 'dayjs';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/es';
import isNil from 'lodash-es/isNil';

import type { DateUnitValue, DaysOfWeekValues } from '@hems/util/src/constant/date';
import {
  DASH_DATE_FORMAT,
  DATE_UNIT,
  DAYS_OF_WEEK,
  GENERAL_DATE_FORMAT,
  NORMAL_DATE_FORMAT,
  NORMAL_YEAR_FORMAT,
  ONE_HOUR_VALUE_IN_MINUTE,
  START_TIME_VALUE,
  US_FORMAT_MAP,
  US_FORMAT_MAP_WITH_TIMEZONE,
  UTC_TIME_ZONE,
  DEFAULT_TIMEZONE,
  HOURLY_DATE_FORMAT,
  MONTHLY_DATE_FORMAT,
  YEARLY_DATE_FORMAT,
  NORMAL_DATE_HOUR_MIN_FORMAT,
  TIME_FORMAT,
  DATE_OFFSET_REGEX,
  UTC_OFFSET,
  TIMEZONE_SHORT_FORMAT,
} from '@hems/util/src/constant/date';
import { GRAPH_INTERVAL, GRAPH_TERM_UNIT } from '@hems/util/src/constant/graph';
import type { LanguageValue } from '@hems/util/src/constant/languages';
import { DEFAULT_LANGUAGE, INTL_LOCALE_MAP, LANGUAGE } from '@hems/util/src/constant/languages';
import { MAPPER_PERIOD_DATA } from '@hems/util/src/constant/mapper';
import { getLangCd, padLeft, isNull } from '@hems/util/src/helper/helper';
import { isLocaleValueType } from '@hems/util/src/helper/tsguardHelper';

import type { DateFormatConfig, DateFormatKey, Period, TermUnit, TimeFormat, DayjsSetObject } from 'hems';

import type { Interval } from 'hems/report';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(advancedFormat);

/** 시작 날짜 조회 (Dayjs 타입 return) */
export const getStartOf = (date: Date | number, unitOfTime: OpUnitType): Dayjs => dayjs(date).startOf(unitOfTime);

/** 끝 날짜 조회 (Dayjs 타입 return) */
export const getEndOf = (date: Date | number, unitOfTime: OpUnitType): Dayjs => dayjs(date).endOf(unitOfTime);

/** 날짜 포맷 변환 - Intl 내장 객체 적용 */
export const getDateFormatter = (langCd: string, options: Intl.DateTimeFormatOptions = {}): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat(isLocaleValueType(langCd) ? INTL_LOCALE_MAP[langCd] : DEFAULT_LANGUAGE, options);

/** 특정 날짜의 끝 시간 조회 */
export const getHourlyEndValue = (date: Date): Date => getEndOf(date, DATE_UNIT.DAY).toDate();

/** 현재 Date 조회 */
export const now = (): Date => new Date();

/** 현재 Date 조회 - 년, 월, 일까지 적용 (시간 제외) */
export const today = (): Date => dayjs().startOf(DATE_UNIT.DAY).toDate();

/** 현재 Date 포맷 - Dayjs 적용 */
export const formatToday = (format: string): string => dayjs().format(format);

/** 어제 Date 조회 - 년, 월, 일까지 적용 (시간 제외) */
export const getYesterday = (): Date => dayjs().subtract(1, DATE_UNIT.DAY).startOf(DATE_UNIT.DAY).toDate();

/** 2일 전 Date 조회 - 년, 월, 일까지 적용 */
export const getTwoDaysAgo = (): Date => dayjs().subtract(2, DATE_UNIT.DAY).startOf(DATE_UNIT.DAY).toDate();

/** 날짜 조회 시 시작 Date 조회 (오늘 기준 6일 전) */
export const getSixDaysAgo = (): Date => dayjs().subtract(6, DATE_UNIT.DAY).startOf(DATE_UNIT.DAY).toDate();

/** 오늘 기준 끝 시간 조회 */
export const getEndDate = (): Date => dayjs().endOf(DATE_UNIT.DAY).toDate();

/** 날짜 포맷 변환 - Dayjs 적용 (기본값: 'YYYYMMDD') */
export const formatDate = (date: dayjs.ConfigType, format = NORMAL_DATE_FORMAT): string => dayjs(date).format(format);

/** 현재 날짜 포맷 변환 - 정규식, Intl 내장 객체 적용 */
export const getSimpleDateFormat = (date: string, type: string, lang?: string, installedDate?: string): string => {
  const langCd = lang && isLocaleValueType(lang) ? INTL_LOCALE_MAP[lang] : DEFAULT_LANGUAGE;

  let now = new Date();

  if (date) {
    const year = Number(date.substring(0, 4));
    const month = Number(date.substring(4, 6)) > 0 ? Number(date.substring(4, 6)) : 1;
    const day = Number(date.substring(6, 8)) > 0 ? Number(date.substring(6, 8)) : 1;

    now = new Date(year, month - 1, day);
  }

  let options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
  let replaceValue = '$2 $1';
  let additionalValue = ' ';

  if (type === MAPPER_PERIOD_DATA.MONTH) {
    options = { year: 'numeric', month: 'short' };
    replaceValue = '$1 $2';
  } else if (type === MAPPER_PERIOD_DATA.YEAR) {
    options = { year: 'numeric' };
  } else if (type === MAPPER_PERIOD_DATA.LIFETIME) {
    options = { year: 'numeric' };
    const today = new Date();
    additionalValue = ` - ${today.getUTCFullYear()}`;
  }

  const re = /(\w+)\s(\w+)/;

  return type === MAPPER_PERIOD_DATA.LIFETIME
    ? dayjs(installedDate).locale(langCd).format(NORMAL_YEAR_FORMAT) + additionalValue
    : now.toLocaleDateString(langCd, options).replace(re, replaceValue);
};

/** Interval에 따른 날짜 포맷 변환 */
export const formatDateByInterval = (date: Date, interval: Interval): string => {
  switch (interval) {
    case GRAPH_INTERVAL.HOURLY:
      return dayjs(date).format(HOURLY_DATE_FORMAT);
    case GRAPH_INTERVAL.DAILY:
      return dayjs(date).format(NORMAL_DATE_FORMAT);
    case GRAPH_INTERVAL.MONTHLY:
      return dayjs(date).format(MONTHLY_DATE_FORMAT);
    case GRAPH_INTERVAL.YEARLY:
      return dayjs(date).format(YEARLY_DATE_FORMAT);
  }
};

/**
 * offset이 포함된 날짜 문자열에서 시 단위만 있고 분 단위가 없는 경우 :00을 붙여서 반환
 * @param dateString - offset이 포함된 날짜 문자열 (예: "2025-01-01T10:00:00+09", "2025-01-01T10:00:00-08", "2025-01-01T10:00:00Z")
 * @returns 수정된 날짜 문자열
 */
export const normalizeOffsetInDateString = (dateString: string): string => {
  const match = dateString.match(DATE_OFFSET_REGEX);

  if (!match) return dateString;

  const offset = match[1];

  if (offset === UTC_OFFSET) {
    return dateString;
  }

  const sign = offset.charAt(0);
  const hours = offset.substring(1);
  const hasMinutes = offset.includes(':');

  if (!hasMinutes) {
    return dateString.replace(DATE_OFFSET_REGEX, `${sign}${hours}:00`);
  }

  return dateString;
};

/** Interval에 따른 timestamp 조회 */
export const getTime = (date: string | number, interval: Interval): number => {
  if (typeof date === 'number') return 0;

  switch (interval) {
    case GRAPH_INTERVAL.HOURLY:
      return new Date(
        Number(date.substring(0, 4)),
        Number(date.substring(4, 6)) - 1,
        Number(date.substring(6, 8)),
        Number(date.substring(8, 10))
      ).getTime();
    case GRAPH_INTERVAL.DAILY:
      return new Date(
        Number(date.substring(0, 4)),
        Number(date.substring(4, 6)) - 1,
        Number(date.substring(6, 8))
      ).getTime();
    case GRAPH_INTERVAL.MONTHLY:
      return new Date(Number(date.substring(0, 4)), Number(date.substring(4, 6)) - 1).getTime();
    case GRAPH_INTERVAL.YEARLY:
      return dayjs(date, YEARLY_DATE_FORMAT).toDate().getTime();
  }
};

const getDateFormatKey = (isDay?: boolean, isTime?: boolean, isSecond?: boolean): DateFormatKey => {
  const day = isDay ? 'day' : 'none';

  let timeType: TimeFormat = 'none';

  if (isTime) {
    timeType = isSecond ? 'timeWithSecond' : 'time';
  }

  return `${day}_${timeType}`;
};

/** Date 포맷 조회 */
export const getLocalDateFormat = ({ isTime, isDay, isMonthly, isSecond }: DateFormatConfig): string => {
  if (isMonthly) {
    return 'MMM, YYYY';
  }

  return US_FORMAT_MAP[getDateFormatKey(isDay, isTime, isSecond ?? true)];
};

/** Date 포맷(타임존 포함) 조회 */
export const getDateFormatWithTimeZone = ({ isTime, isDay, isSecond }: DateFormatConfig): string =>
  US_FORMAT_MAP_WITH_TIMEZONE[getDateFormatKey(isDay, isTime, isSecond ?? true)];

/** 날짜 스트링 조회  'YYYY-MM-DD' */
export const getFormattedDate = (date: Date): string => dayjs(date).format(DASH_DATE_FORMAT);

/** 날짜+시간 스트링 조회 'YYYY-MM-DD HH:mm:ss' */
export const getFormattedDateTime = (date: Date): string => dayjs(date).format(GENERAL_DATE_FORMAT);

/** 기본 타임존 날짜 조회 */
export const getDefaultDateTime = (date: Date): Dayjs => dayjs.tz(date, DEFAULT_TIMEZONE);

/**
 * locale 날짜(date)값에 대해 GMT(UTC+0) String 타입 포맷(YYYY-MM-DD HH:mm:ss)으로 변환
 * @param date
 * @returns
 */
export const convertDefaultToGMTDateTime = (date: Date): string => {
  const gmtDateTime = getDefaultDateTime(date).clone().tz(UTC_TIME_ZONE).toDate();

  return getFormattedDateTime(gmtDateTime);
};

export const getTimestampToLocaleDateString = (timestamp: string, format = DASH_DATE_FORMAT): string =>
  dayjs.unix(Number(timestamp)).tz(DEFAULT_TIMEZONE).format(format);

/**
 * timestamp 값에 대해 locale 날짜 Date 타입으로 변환
 * @param timestamp
 * @returns
 */
export const getTimestampToLocaleDate = (timestamp: string): Date =>
  new Date(getTimestampToLocaleDateString(timestamp));

export const getTimeZoneOffset = (withTime = false, date?: number | string): string => {
  const offsetMinutes = date ? dayjs.tz(date, DEFAULT_TIMEZONE).utcOffset() : dayjs.tz(DEFAULT_TIMEZONE).utcOffset();
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;

  let offsetString = offsetMinutes < 0 ? `-${padLeft(hours, 2)}` : `+${padLeft(hours, 2)}`;
  if (withTime) {
    offsetString = `${offsetString}:${padLeft(minutes, 2)}`;
  }

  return offsetString;
};

/** TimeZone의 short term 조회 */
export const getShortFormTimeZone = (timezone: string): string => dayjs().tz(timezone).format(TIMEZONE_SHORT_FORMAT);

/** TimeZone의 long term 조회 */
export const getLongFormTimeZone = (timezone: string): string => {
  try {
    return (
      new Intl.DateTimeFormat(LANGUAGE.EN, {
        timeZoneName: 'long',
        timeZone: timezone,
      })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value || timezone
    );
  } catch {
    return timezone;
  }
};

/** UTC 기준 날짜 포맷 변환 */
export const getUtcDate = (date: number | string, formatConfig: DateFormatConfig): string => {
  let normalizedDate = date;

  if (typeof date === 'string') {
    normalizedDate = normalizeOffsetInDateString(date);
  }

  return dayjs.utc(normalizedDate).tz(UTC_TIME_ZONE).format(getDateFormatWithTimeZone(formatConfig));
};

/** Date with Timezone 날짜 포맷 변환 */
export const getTimezoneDate = (date: Date | number | string, formatConfig: DateFormatConfig): string => {
  const { needUtcConvert, timezone = UTC_TIME_ZONE } = formatConfig;
  const lang = getLangCd() ?? DEFAULT_LANGUAGE;
  const dateFormat = getDateFormatWithTimeZone(formatConfig);
  dayjs.locale(INTL_LOCALE_MAP[lang]);

  let normalizedDate = date;

  if (typeof date === 'string') {
    normalizedDate = normalizeOffsetInDateString(date);
  }

  if (needUtcConvert) {
    return dayjs(normalizedDate).utc().tz(timezone).format(dateFormat);
  }

  return dayjs.tz(normalizedDate, timezone).format(dateFormat);
};

/** Date의 시작 시간 조회 */
export const getStartOfDate = (date: Date | number): Date => getStartOf(date, DATE_UNIT.DAY).toDate();

/** Date의 끝 시간 조회 */
export const getEndOfDate = (date: Date | number): Date => getEndOf(date, DATE_UNIT.DAY).toDate();

/** 오늘 시작, 끝 Date 조회 (포맷 변환 포함) */
export const getTodayStartEndDateTimes = (timeZone: string, format?: string): string[] => {
  const startDate = dayjs().tz(timeZone).startOf(DATE_UNIT.DAY).format(format);
  const endDate = dayjs().tz(timeZone).endOf(DATE_UNIT.DAY).format(format);

  return [startDate, endDate];
};

/** local Date String */
export const getLocalDate = (date: Date | number | string, formatConfig: DateFormatConfig): string => {
  const { isTime, isDay, isSecond, isMonthly, needUtcConvert, needTimezoneConvert, timezone } = formatConfig;
  const lang: LanguageValue = getLangCd() ?? DEFAULT_LANGUAGE;
  const dateFormat = getLocalDateFormat({ isTime, isDay, isSecond, isMonthly });

  dayjs.locale(INTL_LOCALE_MAP[lang]);

  let normalizedDate = date;

  if (typeof date === 'string') {
    normalizedDate = normalizeOffsetInDateString(date);
  }

  if (needUtcConvert) {
    if (needTimezoneConvert && !isNull(timezone)) {
      return dayjs.utc(normalizedDate).tz(timezone, false).format(dateFormat);
    }

    return dayjs.utc(normalizedDate).format(dateFormat);
  }
  if (needTimezoneConvert && !isNull(timezone)) {
    return dayjs.tz(normalizedDate, timezone).format(dateFormat);
  }

  return dayjs(normalizedDate).format(dateFormat);
};

/** Graph Date 파라미터 */
export const getGraphDateParameter = (date: Date | number | string, termUnit: TermUnit): string => {
  if (termUnit === GRAPH_TERM_UNIT.MINUTE) {
    return dayjs(date).format(NORMAL_DATE_FORMAT);
  }

  return dayjs(date).format(HOURLY_DATE_FORMAT);
};

/** Graph Date String */
export const getConvertedGraphDateString = (date: string, termUnit: TermUnit): string => {
  const lang = getLangCd() ?? DEFAULT_LANGUAGE;
  const dateFormat =
    termUnit === GRAPH_TERM_UNIT.MINUTE
      ? TIME_FORMAT
      : getLocalDateFormat({ isTime: true, isDay: false, isSecond: false });
  dayjs.locale(INTL_LOCALE_MAP[lang]);

  return dayjs(date, NORMAL_DATE_HOUR_MIN_FORMAT).format(dateFormat);
};

/** 두 날짜의 차이 조회 (일, 월, 년) */
export const getDateDifference = (date: Period, dateUnit: DateUnitValue): number =>
  dayjs(date.end).diff(dayjs(date.start), dateUnit);

/** 두 날짜 간 일 수 카운트 */
export const countDaysBetweenDates = (date: Period): number =>
  dayjs(date.end).diff(dayjs(date.start), DATE_UNIT.DAY) + 1;

/** 시간 설정 옵션으로 변경된 날짜 조회 */
export const getDateWithAdjustedTime = (date: Date | number | string, adjustedOptions: DayjsSetObject): Date => {
  let dayjsDate = dayjs(date);

  if (!isNil(adjustedOptions.hour)) dayjsDate = dayjsDate.hour(adjustedOptions.hour);
  if (!isNil(adjustedOptions.minute)) dayjsDate = dayjsDate.minute(adjustedOptions.minute);
  if (!isNil(adjustedOptions.second)) dayjsDate = dayjsDate.second(adjustedOptions.second);
  if (!isNil(adjustedOptions.millisecond)) dayjsDate = dayjsDate.millisecond(adjustedOptions.millisecond);

  return dayjsDate.toDate();
};

/** 타임존에 해당하는 현재 시간 조회 */
export const getCurrentHourByTimezone = (timezone: string): number => dayjs().tz(timezone).hour();

/** 브라우저 타임존 조회 */
export const getBrowserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/** 오늘 기준 빼기 */
export const subtractDate = (amount: number, unit: ManipulateType): Date => dayjs().subtract(amount, unit).toDate();

/** 오늘 기준 더하기 */
export const addDate = (amount: number, unit: ManipulateType): Date => dayjs().add(amount, unit).toDate();

/** 시작 시간 기본 옵션 */
export const defaultStartDateTimeOptions: DayjsSetObject = { hour: 0, minute: 0, second: 0, millisecond: 0 };

/** 끝 시간 기본 옵션 */
export const defaultEndDateTimeOptions: DayjsSetObject = { hour: 23, minute: 59, second: 59, millisecond: 999 };

/** 기본 기간 옵션 (3일) */
export const defaultPeriod: Period = {
  start: getTwoDaysAgo(),
  end: getDateWithAdjustedTime(now(), defaultEndDateTimeOptions),
};

/** 31일 기간 옵션 */
export const thirtyOneDaysPeriod: Period = {
  start: getDateWithAdjustedTime(subtractDate(30, DATE_UNIT.DAY), defaultStartDateTimeOptions),
  end: getDateWithAdjustedTime(now(), defaultEndDateTimeOptions),
};

/** 6달 기간 옵션 */
export const sixMonthsPeriod: Period = {
  start: getDateWithAdjustedTime(subtractDate(6, DATE_UNIT.MONTH), defaultStartDateTimeOptions),
  end: getDateWithAdjustedTime(now(), defaultEndDateTimeOptions),
};

/** timezone offset을 제외한 날짜 부분만 추출 */
export const extractDateWithoutOffset = (dateString: string): string => dateString.replace(DATE_OFFSET_REGEX, '');

/** 기간에 포함된 날짜인지 체크 */
export const isDateInRange = (targetDate: Date, period: Period): boolean =>
  dayjs(targetDate).isBetween(period.start, period.end, null, '[]');

/** 이번 주의 시작일 조회 */
export const startOfWeek: Date = dayjs().startOf(DATE_UNIT.WEEK).toDate();

/** 특정 Date + N days 조회 */
export const getAddedDate = (date: Date, days: number): Date => dayjs(date).add(days, DATE_UNIT.DAY).toDate();

const weekdayList: DaysOfWeekValues[] = Object.values(DAYS_OF_WEEK).filter(
  (day) => day !== DAYS_OF_WEEK.SATURDAY && day !== DAYS_OF_WEEK.SUNDAY
);

const weekendList: DaysOfWeekValues[] = Object.values(DAYS_OF_WEEK).filter(
  (day) => day === DAYS_OF_WEEK.SATURDAY || day === DAYS_OF_WEEK.SUNDAY
);

const everyDayList: DaysOfWeekValues[] = Object.values(DAYS_OF_WEEK);

/** 주중 요일 배열인지 여부 */
export const isWeekdayList = (daysOfWeek: DaysOfWeekValues[]): boolean =>
  weekdayList.every((day) => daysOfWeek.includes(day)) && !weekendList.some((day) => daysOfWeek.includes(day));

/** 주말 요일 배열인지 여부 */
export const isWeekendList = (daysOfWeek: DaysOfWeekValues[]): boolean =>
  weekendList.every((day) => daysOfWeek.includes(day)) && !weekdayList.some((day) => daysOfWeek.includes(day));

/** 모든 요일 배열인지 여부 */
export const isEverydayList = (daysOfWeek: DaysOfWeekValues[]): boolean =>
  everyDayList.every((day) => daysOfWeek.includes(day));

/** 날짜 - Date 타입 객체 */
export const getDateByDateString = (dateString: string): Date => dayjs(dateString).toDate();

export const getCurrentTimestamp = (): number => new Date().getTime();

/** 00:00:00 의 형식의 시간을 다른 시간으로 변경  */
export const changeTimeToOthers = (time: string, format: string = START_TIME_VALUE): string =>
  time.replace(/\d{2}:\d{2}:\d{2}$/, format);

/**
 * 시간(HHmm)을 분 단위 값으로 변환
 * @param colect_dt
 * @returns
 */
export const convertHourToMin = (date: string): number => Number(date.slice(-4, -2)) * 60 + Number(date.slice(-2));

/**
 * 분 단위 값을 시간(HH:mm)으로 변환
 * @param colect_dt
 * @returns
 */
export const convertMinToHour = (value: number, hasUnit = true): string => {
  let hour = String(Math.floor(value / ONE_HOUR_VALUE_IN_MINUTE));
  let min = String(value % ONE_HOUR_VALUE_IN_MINUTE);
  min = Number(min) < 10 ? `0${min}` : min;
  hour = Number(hour) < 10 ? `0${hour}` : hour;

  const unit = Number(hour) < 12 ? 'AM' : 'PM';

  if (hasUnit) {
    min = `${min}${unit}`;
  }

  return `${hour}:${min}`;
};

export const convertMinTo12Hour = (value: number, hasUnit = true): string => {
  const hour = Math.floor(value / ONE_HOUR_VALUE_IN_MINUTE);
  const min = value % ONE_HOUR_VALUE_IN_MINUTE;

  /** 오후 12시, 밤 24시 일때는 hour를 12로 할당 */
  const convertedTo12Hour = hour % 12 || 12;

  const convertedHour = padLeft(convertedTo12Hour, 2);
  const convertedMin = padLeft(min, 2);

  const timeUnit = hour < 12 || hour == 24 ? 'AM' : 'PM';

  return `${convertedHour}:${convertedMin}${hasUnit ? timeUnit : ''}`;
};

/** 특정 date 기준 날짜 빼기 */
export const subtractFromDate = (date: Date | number, amount: number, unit: ManipulateType): Date =>
  dayjs(date).subtract(amount, unit).toDate();

/** 특정 date 기준 날짜 더하기 */
export const addFromDate = (date: Date | number, amount: number, unit: ManipulateType): Date =>
  dayjs(date).add(amount, unit).toDate();

/** 타임존 오프셋을 포함한 날짜 문자열을 로컬 날짜 문자열로 변환 (타임존 오프셋 유지) */
export const formatDateWithOriginalOffset = (dateString: string, formatConfig: DateFormatConfig): string => {
  const { isTime, isDay, isSecond, isMonthly, needUtcConvert } = formatConfig;
  const lang: LanguageValue = getLangCd() ?? DEFAULT_LANGUAGE;
  const dateFormat = getLocalDateFormat({ isTime, isDay, isSecond, isMonthly });
  dayjs.locale(INTL_LOCALE_MAP[lang]);

  if (needUtcConvert) {
    return dayjs.utc(dateString).format(dateFormat);
  }

  const offsetMatch = dateString.match(DATE_OFFSET_REGEX);

  if (!offsetMatch) {
    return dayjs(dateString).format(dateFormat);
  }

  const offset = offsetMatch[1];
  const dateWithoutOffset = dateString.replace(DATE_OFFSET_REGEX, '');

  if (offset === UTC_OFFSET) {
    return dayjs.utc(dateWithoutOffset).format(dateFormat);
  }

  return dayjs(dateWithoutOffset).format(dateFormat);
};

/** 1년 기간인지 체크 */
export const isValidOneYearPeriod = (date: Period): boolean => {
  const oneYearAgo = dayjs(date.end).subtract(1, DATE_UNIT.YEAR).startOf(DATE_UNIT.DAY);

  const start = dayjs(date.start, DASH_DATE_FORMAT);
  const end = dayjs(date.end, DASH_DATE_FORMAT);

  if (!start.isValid() || !end.isValid()) return false;
  if (start.isAfter(end)) return false;

  if (start.isBefore(oneYearAgo)) return false;

  return true;
};
